import React from 'react';
import semverCompare from 'semver-compare';
import {
  AGENT_HEALTH_INTENTS,
  CAPABILITY_HEALTH,
  CAPABILITY_HEALTH_INTENTS,
  CLUSTER_HEALTH_INTENTS,
  CLUSTER_HEALTH_LABELS,
  Statuses
} from '@kentik/ui-shared/agents/constants';
import { StatusTag } from 'app/views/metrics/tables/StatusIndicators';
import { Text } from 'core/components';
import LabelValue from 'app/views/metrics/components/LabelValue';
import SudoTaggedText from 'app/components/SudoTaggedText';

/**
 * Consistent SudoTaggedText wrapper for Kentik Agent views.
 * Bakes in the `flexShrink: 0` tag styling used across all agent sudo tags.
 * @param {Object} props
 * @param {'right'|'left'} [props.tagPosition='left'] - Tag placement relative to text.
 */
export const AgentSudoTaggedText = ({ tagProps, tagPosition = 'left', ...rest }) => (
  <SudoTaggedText
    {...rest}
    tagPosition={tagPosition}
    width="fit-content"
    tagProps={{ ...tagProps, style: { ...tagProps?.style, flexShrink: 0 } }}
  />
);

export const mapIntentFromAgentHealth = (model) => {
  if (model.get('status') === Statuses.NOT_AUTHORIZED) {
    return 'none';
  }
  const health = model.get('health');
  return AGENT_HEALTH_INTENTS[health] || '';
};

export const mapIntentFromCapabilityHealth = (model, options = {}) => {
  const { showSuccessIntent = false } = options;
  const { health } = model;
  const intent = CAPABILITY_HEALTH_INTENTS[health];

  if (intent === undefined) {
    return '';
  }

  // For HEALTHY, respect the showSuccessIntent option
  if (health === CAPABILITY_HEALTH.CAPABILITY_HEALTH_STATE_HEALTHY) {
    return showSuccessIntent ? 'success' : 'none';
  }

  return intent;
};

export const ClusterStatusTag = ({ healthState }) => {
  if (!healthState || CLUSTER_HEALTH_LABELS[healthState] === undefined) {
    return null;
  }
  const intent = CLUSTER_HEALTH_INTENTS[healthState] || 'none';
  const value = `${CLUSTER_HEALTH_LABELS[healthState]} `;
  return (
    <StatusTag minimal intent={intent} minWidth={105}>
      {value}
    </StatusTag>
  );
};

export const giveRemainingOptions = (currentList, thisValue, form) => {
  const allData = form.getValue('cmdLineArgsMultiSelect') || [];
  const currentlychoicesList = allData
    .filter((choice) => choice.cmdLineOption && choice.cmdLineOption !== thisValue)
    .map((choice) => choice.cmdLineOption);

  return currentList.filter((choice) => !!choice?.value && !currentlychoicesList.includes(choice.value));
};

/**
 * Compares semantic versions and determines if the current version satisfies the constraint
 * @param {string} currentVersion - The current capability version (e.g., "1.2.3")
 * @param {string} minVersion - The minimum required version (optional)
 * @param {string} maxVersion - The maximum allowed version (optional)
 * @returns {boolean} True if the version satisfies the constraints
 */
export const satisfiesVersionConstraints = (currentVersion, minVersion, maxVersion) => {
  if (!currentVersion) {
    return false;
  }

  // Clean version strings - remove any non-semver characters
  const cleanCurrentVersion = currentVersion.replace(/^v/, '');
  const cleanMinVersion = minVersion ? minVersion.replace(/^v/, '') : null;
  const cleanMaxVersion = maxVersion ? maxVersion.replace(/^v/, '') : null;

  try {
    // Check minimum version constraint
    if (cleanMinVersion && semverCompare(cleanCurrentVersion, cleanMinVersion) < 0) {
      return false;
    }

    // Check maximum version constraint
    if (cleanMaxVersion && semverCompare(cleanCurrentVersion, cleanMaxVersion) > 0) {
      return false;
    }

    return true;
  } catch (error) {
    console.warn('Error comparing versions:', error, {
      currentVersion: cleanCurrentVersion,
      minVersion: cleanMinVersion,
      maxVersion: cleanMaxVersion
    });
    // If version comparison fails, include the field by default
    return true;
  }
};

/**
 * Filters capability configuration metadata based on the capability version
 * @param {Object} configMetadata - The configuration metadata object
 * @param {string} capabilityVersion - The current capability version
 * @returns {Object} Filtered configuration metadata
 */
export const filterConfigMetadataByVersion = (configMetadata, capabilityVersion) => {
  if (!configMetadata || !capabilityVersion) {
    return configMetadata;
  }

  const filteredMetadata = {};

  Object.entries(configMetadata).forEach(([fieldKey, fieldConfig]) => {
    const { minVersion, maxVersion } = fieldConfig;

    // If no version constraints are specified, include the field
    if (!minVersion && !maxVersion) {
      filteredMetadata[fieldKey] = fieldConfig;
      return;
    }

    // Check if the capability version satisfies the constraints
    if (satisfiesVersionConstraints(capabilityVersion, minVersion, maxVersion)) {
      filteredMetadata[fieldKey] = fieldConfig;
    }
    // If version constraints are not satisfied, the field is excluded
  });

  return filteredMetadata;
};

/**
 * Renders an array of { key, value } pairs as LabelValue components.
 * @param {Array<{ key: string|React.ReactNode, value: React.ReactNode, id?: string }>} pairs
 * @param {{ sudoTagged?: boolean, labelWidth?: number, alignItems?: string }} options
 */
export const renderKeyValuePairs = (pairs, { sudoTagged = false, labelWidth = 200, alignItems } = {}) =>
  pairs.map(({ key, value, id, sudoRequired = false }) => {
    const isSudo = sudoTagged || sudoRequired;
    return (
      <LabelValue
        key={`${id || key}-${value}`}
        alignItems="center"
        label={
          isSudo ? (
            <AgentSudoTaggedText mr={1} maxWidth={labelWidth}>
              <Text ellipsis title={key}>
                {key}
              </Text>
            </AgentSudoTaggedText>
          ) : (
            <Text ellipsis title={key} mr={1}>
              {key}
            </Text>
          )
        }
        value={value}
        mb={0}
        labelWidth={labelWidth}
        {...(alignItems ? { alignItems } : {})}
      />
    );
  });

// ------------------------------------------------------------
// dependsOn schema
// {
//   dependsOn: {
//     if: {
//       condition: ConditionExpr,
//       then: FieldMap,
//       else: ( { if: {...} } | FieldMap )
//     }
//   }
// }
// FieldMap: { fieldKey: { value?, type?, label?, description?, placeholder?, rules? }, ... }
// ConditionExpr: {eq|neq|present|not_present|and|or}
//
// Example:
// "exampleVar" is included only if "dns" is present AND "host" equals "0.0.0.0"
// It also injects variable "foo" with value "bar" and label "Foo"
// {
//   "name": "exampleVar",
//   ...
//   "dependsOn": {
//     "if": {
//       "condition": {
//         "and": [
//           { "present": "dns" },
//           { "eq": { "host" : "0.0.0.0" } }
//         ]
//       },
//       "then": {
//         "foo": {
//           "label": "Foo",
//           "value": "bar"
//         }
//       }
//     }
//   }
// }
// ------------------------------------------------------------

/**
 * Recursively evaluate a condition expression against the argsObject.
 * @param {Object} expr - Condition expression node.
 * @param {Object} argsObject - Current capability args.
 * @param {Array<string>} warnings - Accumulates non-fatal issues.
 * @returns {boolean}
 */
const evaluateConditionExpr = (expr, argsObject, warnings) => {
  if (!expr || typeof expr !== 'object') {
    warnings.push('Invalid condition expression (not an object)');
    return false;
  }

  // Equality / inequality
  if (expr.eq) {
    const entries = Object.entries(expr.eq);
    if (entries.length !== 1) {
      warnings.push('eq operator must have exactly one key');
      return false;
    }
    const [k, v] = entries[0];
    return argsObject[k] === v;
  }
  if (expr.neq) {
    const entries = Object.entries(expr.neq);
    if (entries.length !== 1) {
      warnings.push('neq operator must have exactly one key');
      return false;
    }
    const [k, v] = entries[0];
    return argsObject[k] !== v;
  }
  // Presence operators
  if (expr.present) {
    if (typeof expr.present !== 'string') {
      warnings.push('present value must be a string key');
      return false;
    }
    return argsObject[expr.present] !== undefined;
  }
  if (expr.not_present) {
    if (typeof expr.not_present !== 'string') {
      warnings.push('not_present value must be a string key');
      return false;
    }
    return argsObject[expr.not_present] === undefined;
  }
  if (expr.and) {
    if (!Array.isArray(expr.and) || expr.and.length === 0) {
      warnings.push('and operator requires a non-empty array');
      return false;
    }
    for (const child of expr.and) {
      if (!evaluateConditionExpr(child, argsObject, warnings)) {
        return false;
      }
    }
    return true;
  }
  if (expr.or) {
    if (!Array.isArray(expr.or) || expr.or.length === 0) {
      warnings.push('or operator requires a non-empty array');
      return false;
    }
    for (const child of expr.or) {
      if (evaluateConditionExpr(child, argsObject, warnings)) {
        return true;
      }
    }
    return false;
  }

  warnings.push(`Unknown condition operator: ${JSON.stringify(expr)}`);
  return false;
};

/**
 * Validates a FieldMap entry and returns sanitized metadata.
 * @param {string} key
 * @param {Object} def - Field definition { value?, type?, label?, description?, placeholder?, rules? }
 * @param {Array<string>} warnings
 * @returns {Object|null} - { key, value, type, label, description } or null if invalid
 */
const validateFieldDef = (key, def, warnings) => {
  if (!def || typeof def !== 'object') {
    warnings.push(`Field '${key}' definition must be an object.`);
    return null;
  }
  const {
    value,
    default: defaultValue,
    type: declaredType,
    label,
    description,
    placeholder,
    rules: declaredRules,
    required
  } = def;
  // Use value if provided, otherwise fall back to default
  const resolvedValue = value !== undefined ? value : defaultValue;
  // Infer type if not declared
  let inferredType;
  if (resolvedValue !== undefined) {
    // eslint-disable-next-line no-nested-ternary
    inferredType = Array.isArray(resolvedValue)
      ? 'array'
      : resolvedValue !== null && typeof resolvedValue === 'object'
        ? 'object'
        : typeof resolvedValue; // string | number | boolean | undefined
  }
  const finalType = declaredType && typeof declaredType === 'string' ? declaredType : inferredType;
  if (declaredType && inferredType && declaredType !== inferredType) {
    warnings.push(
      `Field '${key}' declared type '${declaredType}' mismatches inferred '${inferredType}'. Using declared type.`
    );
  }
  // Build rules array, including 'required' if specified in the schema
  const rules = [...(declaredRules || [])];
  if (required && !rules.includes('required')) {
    rules.push('required');
  }
  return { key, value: resolvedValue, default: defaultValue, type: finalType, label, description, placeholder, rules };
};

/**
 * Evaluates a nested if node returning selected field map & branch info.
 * @param {Object} ifNode - The object under root 'if'.
 * @param {Object} argsObject
 * @param {Array<string>} warnings
 * @returns {{fields: Object, branchPath: string[]}}
 */
const evaluateIfNode = (ifNode, argsObject, warnings, branchPath = []) => {
  if (!ifNode || typeof ifNode !== 'object') {
    warnings.push('Invalid if node (not an object).');
    return { fields: {}, branchPath };
  }
  const { condition, then, else: elseNode } = ifNode;
  const condResult = evaluateConditionExpr(condition, argsObject, warnings);
  if (condResult) {
    return { fields: then && typeof then === 'object' ? then : {}, branchPath: [...branchPath, 'then'] };
  }
  if (elseNode && typeof elseNode === 'object') {
    // Nested else-if
    if (elseNode.if) {
      return evaluateIfNode(elseNode.if, argsObject, warnings, [...branchPath, 'else']);
    }
    // Terminal else (field map)
    return { fields: elseNode, branchPath: [...branchPath, 'else'] };
  }
  // No else provided
  return { fields: {}, branchPath: [...branchPath, 'none'] };
};

/**
 * evaluateDependsOn processes the dependsOn tree and returns applied fields.
 * @param {Object} dependsOnTree - Object containing root 'if'.
 * @param {Object} argsObject - Current args.
 * @returns {{ appliedFields: Object, appliedMetadata: Array<Object>, branchPath: string[], warnings: Array<string> }}
 */
export const evaluateDependsOn = (dependsOnTree, argsObject) => {
  const warnings = [];
  if (!dependsOnTree || typeof dependsOnTree !== 'object') {
    return { appliedFields: {}, appliedMetadata: [], branchPath: [], warnings: ['dependsOn must be an object'] };
  }
  const rootIf = dependsOnTree.if;
  if (!rootIf || typeof rootIf !== 'object') {
    return {
      appliedFields: {},
      appliedMetadata: [],
      branchPath: [],
      warnings: ['dependsOn.if missing or invalid']
    };
  }
  const { fields, branchPath } = evaluateIfNode(rootIf, argsObject, warnings);
  const appliedFields = {};
  const appliedMetadata = [];
  Object.entries(fields || {}).forEach(([key, def]) => {
    const validated = validateFieldDef(key, def, warnings);
    if (!validated) {
      return;
    }
    // Inject provided value OR null when undefined
    appliedFields[key] = validated.value !== undefined ? validated.value : null;
    appliedMetadata.push({
      key,
      type: validated.type,
      label: validated.label,
      description: validated.description,
      placeholder: validated.placeholder,
      rules: validated.rules,
      default: validated.default
    });
  });
  return { appliedFields, appliedMetadata, branchPath, warnings };
};
